import type { CaptureStage } from '@kyc/shared';
import { ApiClient, ApiError } from '../api/client.js';
import { uploadMimeType } from '../capture/recorder.js';

/**
 * §21 upload client: SHA-256 first, then init → sequential chunks → idempotent complete,
 * with retry/backoff (in ApiClient) and §25 token re-issue on upload-token expiry.
 * The blob is never discarded before the server acknowledges completion.
 */

export interface UploadProgress {
  stage: CaptureStage;
  sentChunks: number;
  totalChunks: number;
}

export async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export class UploadSupersededError extends Error {
  constructor() {
    super('Upload superseded by a newer attempt');
    this.name = 'UploadSupersededError';
  }
}

export async function uploadStageBlob(
  api: ApiClient,
  stage: CaptureStage,
  blob: Blob,
  recorderMime: string,
  bestFrameHintsMs: number[],
  onProgress?: (p: UploadProgress) => void,
): Promise<{ mediaId: string; sha256: string }> {
  const bytes = await blob.arrayBuffer();
  const sha256 = await sha256Hex(bytes);

  const init = await api.initUpload(stage, {
    declaredSizeBytes: bytes.byteLength,
    mimeType: uploadMimeType(recorderMime),
    sha256,
    clientManifest: { bestFrameTimestampsMs: bestFrameHintsMs.slice(0, 5) },
  });

  let uploadToken = init.uploadToken;
  const { mediaId, chunkSizeBytes, totalChunks } = init;

  const sendChunk = async (index: number): Promise<void> => {
    const chunk = bytes.slice(index * chunkSizeBytes, Math.min((index + 1) * chunkSizeBytes, bytes.byteLength));
    try {
      await api.putChunk(mediaId, index, chunk, uploadToken);
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'TOKEN_INVALID' || err.status === 401)) {
        // §25 — upload URL/token expired: re-issue and retry without recapture.
        const reissued = await api.reissueUploadToken(mediaId);
        uploadToken = reissued.uploadToken;
        await api.putChunk(mediaId, index, chunk, uploadToken);
        return;
      }
      if (err instanceof ApiError && err.code === 'UPLOAD_NOT_ACTIVE') throw new UploadSupersededError();
      throw err;
    }
  };

  for (let i = 0; i < totalChunks; i++) {
    await sendChunk(i);
    onProgress?.({ stage, sentChunks: i + 1, totalChunks });
  }

  try {
    await api.completeUpload(mediaId, sha256, totalChunks, uploadToken);
  } catch (err) {
    if (err instanceof ApiError && (err.code === 'TOKEN_INVALID' || err.status === 401)) {
      const reissued = await api.reissueUploadToken(mediaId);
      await api.completeUpload(mediaId, sha256, totalChunks, reissued.uploadToken);
    } else if (err instanceof ApiError && (err.code === 'UPLOAD_SUPERSEDED' || err.code === 'UPLOAD_NOT_ACTIVE')) {
      throw new UploadSupersededError();
    } else {
      throw err;
    }
  }

  return { mediaId, sha256 };
}
